import * as Sentry from "@sentry/react";
import { useDeferredValue, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Globe, Search } from "lucide-react";
import { ConnectorLogo } from "./ConnectorLogo.tsx";
import { useMutation } from "@tanstack/react-query";

import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { api } from "@/lib/http.ts";
import { Button } from "@/components/ui/button.tsx";
import {
  useWebsiteSignIns,
  websiteGroups,
  filterWebsiteGroups,
  type WebsiteGroup,
  inventoryNotice,
  websiteOperationError,
  clearSessionConfirmation,
  SESSION_EXPLANATION,
  type WebsiteSignInClient,
} from "@/shared/websiteSignIns.ts";
import type { WebsiteSignInsResp, ConnectWebsiteResp } from "@/types/browserSignIn.ts";
import { BrowserLoginForm } from "./BrowserLoginForm.tsx";

const client: WebsiteSignInClient = {
  list: async (signal) =>
    (await api.get<WebsiteSignInsResp>("/api/a/website-sign-ins", { signal })).data,
  createOperationId: () => crypto.randomUUID(),
  connectBrowser: async (websiteUrl) =>
    (await api.post<ConnectWebsiteResp>("/api/a/website-sign-ins/connect-browser", { websiteUrl }))
      .data,
  clearSession: async (request) => {
    await api.post("/api/a/website-sign-ins/clear-session", request);
  },
};

/** Manage saved browser sessions. */
export const WebsiteSignIns = () => {
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [editingLogin, setEditingLogin] = useState<string>();
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string[]>([]);
  const [confirmDomain, setConfirmDomain] = useState<string>();
  const { query, inventoryCurrent, connect, clear } = useWebsiteSignIns(client);
  const groups = useMemo(() => websiteGroups(query.data), [query.data]);
  const deferredSearch = useDeferredValue(search);
  const filtered = useMemo(
    () => filterWebsiteGroups(groups, deferredSearch),
    [groups, deferredSearch],
  );
  const notice = inventoryNotice(query.data, query.isError);
  const openSignIn = useMutation({
    mutationFn: async (result: ConnectWebsiteResp) => result.startPath,
    onSuccess: (url) => window.location.assign(url),
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
  const handleSignIn = (address: string) => {
    connect.mutate(address.trim(), {
      onSuccess: (result) => openSignIn.mutate(result),
    });
  };
  const connecting = connect.isPending || openSignIn.isPending;
  const renderGroup = (group: WebsiteGroup) => {
    const open = expanded.includes(group.domain);
    return (
      <div key={group.domain} className="border-t first:border-t-0">
        <button
          type="button"
          data-statsig-name="website-sign-ins-expand"
          aria-label={`Manage ${group.domain}`}
          aria-expanded={open}
          aria-controls={`website-details-${group.domain}`}
          className="flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() =>
            setExpanded((current) =>
              open
                ? current.filter((domain) => domain !== group.domain)
                : [...current, group.domain],
            )
          }
        >
          <ConnectorLogo
            websiteUrl={`https://${group.domain}`}
            name={group.domain}
            size="sm"
            className="shrink-0 rounded-md"
            fallback={<Globe className="size-4" />}
          />
          <span className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <span className="truncate text-sm font-medium">{group.domain}</span>
            <span className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
              {group.browserData ? <span>Saved browser data</span> : null}

              {!group.browserData ? (
                <span>{inventoryCurrent ? "No saved data" : "Browser data unavailable"}</span>
              ) : null}
            </span>
          </span>
          {open ? (
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
        </button>
        {open ? (
          <div id={`website-details-${group.domain}`} className="space-y-3 px-2 pb-4 pt-1 sm:pl-11">
            <div className="flex flex-wrap gap-2">
              <Button
                data-statsig-name="website-sign-ins-again"
                size="sm"
                variant="outline"
                disabled={connecting}
                onClick={() => handleSignIn(group.domain)}
              >
                Sign in to website
              </Button>
              {group.browserData ? (
                <Button
                  data-statsig-name="website-sign-ins-clear"
                  size="sm"
                  variant="outline"
                  aria-label={`Clear saved session for ${group.domain}`}
                  disabled={clear.isPending || !inventoryCurrent}
                  onClick={() => setConfirmDomain(group.domain)}
                >
                  Clear saved session
                </Button>
              ) : null}
            </div>
            {confirmDomain === group.domain ? (
              <div className="space-y-2 rounded-xl border p-3">
                <p className="text-sm">{clearSessionConfirmation(group.domain)}</p>
                <div className="flex gap-2">
                  <Button
                    data-statsig-name="website-sign-ins-clear-confirm"
                    size="sm"
                    disabled={clear.isPending || !inventoryCurrent}
                    onClick={() =>
                      clear.mutate(group.domain, { onSettled: () => setConfirmDomain(undefined) })
                    }
                  >
                    Clear session
                  </Button>
                  <Button
                    data-statsig-name="website-sign-ins-clear-cancel"
                    size="sm"
                    variant="ghost"
                    disabled={clear.isPending}
                    onClick={() => setConfirmDomain(undefined)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}
            {clear.isError && clear.variables === group.domain ? (
              <p role="alert">{websiteOperationError(clear.error)}</p>
            ) : null}
            {group.browserData ? (
              <div className="space-y-1 break-all text-sm text-muted-foreground">
                {group.browserData.cookieDomains.map((domain) => (
                  <p key={domain}>Cookies: {domain}</p>
                ))}
                {group.browserData.storageOrigins.map((origin) => (
                  <p key={origin}>Site storage: {origin}</p>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };
  return (
    <section
      className="mb-6 space-y-4 rounded-2xl border bg-card p-4"
      aria-label="Website sign-ins"
    >
      <div className="space-y-1">
        <h2 className="font-medium">Add a website</h2>
        <p className="text-sm text-muted-foreground">
          Sign in once so Okthx can use the website in future tasks.
        </p>
      </div>

      <form
        className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          handleSignIn(websiteUrl);
        }}
      >
        <Label htmlFor="website-sign-in-address" className="min-w-0 flex-1 text-sm font-medium">
          Website address
          <Input
            id="website-sign-in-address"
            className="mt-1 text-base"
            value={websiteUrl}
            onChange={(event) => setWebsiteUrl(event.target.value)}
            placeholder="example.com"
            autoCapitalize="none"
            autoCorrect="off"
            required
          />
        </Label>
        <Button
          data-statsig-name="website-sign-ins-add"
          type="submit"
          variant="primary"
          disabled={connecting || !websiteUrl.trim()}
        >
          {connecting ? "Opening browser…" : "Sign in to website"}
        </Button>
      </form>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 text-xs text-muted-foreground">
        <details className="max-w-sm">
          <summary className="cursor-pointer">How sign-in works</summary>
          <p className="pt-2 leading-relaxed">{SESSION_EXPLANATION}</p>
        </details>
      </div>
      {connect.isError || openSignIn.isError ? (
        <p role="alert">{websiteOperationError(connect.error ?? openSignIn.error)}</p>
      ) : null}
      {(query.data?.logins ?? []).map((login) => (
        <div key={login.itemId} className="space-y-2 rounded-xl border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm">{login.origin}</span>
            <Button
              data-statsig-name="website-login-update"
              type="button"
              variant="ghost"
              onClick={() =>
                setEditingLogin(editingLogin === login.origin ? undefined : login.origin)
              }
            >
              Update saved login
            </Button>
          </div>
          {editingLogin === login.origin ? (
            <BrowserLoginForm origin={login.origin} onSaved={() => setEditingLogin(undefined)} />
          ) : null}
        </div>
      ))}
      {query.isPending ? <p>Loading website sign-ins…</p> : null}
      {query.isError || notice ? (
        <div aria-live="polite" className="space-y-1 text-sm">
          <p>{notice ?? "Couldn’t load website sign-ins."}</p>
          <Button
            data-statsig-name="website-sign-ins-refresh"
            variant="link"
            onClick={() => void query.refetch()}
          >
            Refresh
          </Button>
        </div>
      ) : null}
      {query.data && groups.length === 0 && !query.data.logins?.length && !notice ? (
        <p className="text-sm text-muted-foreground">
          No websites yet. Add one above, or ask Okthx to sign in during a task.
        </p>
      ) : null}
      {groups.length > 0 ? (
        <div className="space-y-3 border-t pt-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-medium">Your websites</h3>
            <div className="relative w-full sm:w-60">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                aria-label="Search websites"
                placeholder="Search websites"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-9 pl-9"
              />
            </div>
          </div>
          <div>{filtered.map(renderGroup)}</div>
          {filtered.length === 0 ? (
            <p aria-live="polite" className="py-3 text-sm text-muted-foreground">
              No websites match “{search.trim()}”.
            </p>
          ) : null}
        </div>
      ) : null}
      {query.data?.browserData?.savedAtMs ? (
        <p className="text-xs text-muted-foreground">
          Browser data saved: {new Date(query.data.browserData.savedAtMs).toLocaleString()}
        </p>
      ) : null}

      {clear.isSuccess ? <p aria-live="polite">Saved session cleared.</p> : null}
    </section>
  );
};
